import React, { useMemo, useCallback } from 'react';

import config from 'config';
import { Formik } from 'formik';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { FaChevronDown, FaExclamationCircle } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import { trackRegistrationFailure } from '~/account/actions';
import { ErrorMessageWrapper as DuplicateUserError } from '~/builderQuiz/components/BuilderQuizErrorMessage/ErrorMessage';
import getAutoLoginByEmailRedirect from '~/login/utils/autoLoginByEmail';
import { trackCompleteRegistration } from '~/shared/actions';
import InputFields from '~/shared/components/InputFields';
import featureNames from '~/shared/constants/featureFlags';
import { aggregationKeyMap } from '~/shared/constants/productSizes';
import { getBrandValue } from '~/shared/utils/brandNameHelper';
import { deriveProductCategory } from '~/shared/utils/deriveProductCategory';
import {
  isTermsAndPrivacyCheckboxSupported,
  isTermsAndPrivacyCopySupported,
} from '~/shared/utils/termsAndPrivacy';
import truthyQueryParameter from '~/shared/utils/truthyQueryParameter';
import useHasFeature from '~/shared/utils/useHasFeature';
import { getSecondarySite } from '~/shared/utils/useSecondarySite';
import { useAccountActions, Gender } from '~/techstyle-shared/react-accounts';
import {
  Button,
  Checkbox,
  Dropdown,
  mobile,
  PasswordField,
  TextField,
  useId,
  useProductDetailContext,
} from '~/techstyle-shared/react-components';
import { FormattedMessage, useIntl } from '~/techstyle-shared/react-intl';
import {
  useMarketingActions,
  useReferrerOptions,
} from '~/techstyle-shared/react-marketing';
import {
  loadSession,
  useDomain,
  useSessionDetail,
} from '~/techstyle-shared/redux-core';

import { getSizeByKey } from '../../utils/getSizesForSpeedySignup';
import { useProductDetailCTAContext } from '../ProductDetailCTAs';

const SignupErrors = {
  SIGNUP_FAILED: 'SIGNUP_FAILED',
  EMAIL_REQUIRED: 'EMAIL_REQUIRED',
  INVALID_EMAIL: 'INVALID_EMAIL',
  PASSWORD_REQUIRED: 'PASSWORD_REQUIRED',
  PASSWORD_MIN_CHAR: 'PASSWORD_MIN_CHAR',
  INVALID_ZIPCODE: 'INVALID_ZIPCODE',
  USER_EXISTING: 'USER_EXISTING',
  REFERRER_OPTION_REQUIRED: 'REFERRER_OPTION_REQUIRED',
  CONSENT_REQUIRED: 'CONSENT_REQUIRED',
  DUPLICATE_ACCOUNT: 'DUPLICATE_ACCOUNT',
  GENERIC_ERROR: 'GENERIC_ERROR',
};

const SCRUBS_PRODUCT_CATEGORY_ID = 2080;
const SCRUBS_EXPERIENCE_ID = 'scrubs';

const Form = styled.form`
  input,
  textarea {
    font-size: initial;
  }
`;
const DuplicateUserErrorWrapper = styled(DuplicateUserError)`
  font-size: 14px;
  font-weight: 600;
  padding-top: 30px;
`;

const StyledInputFields = styled(InputFields)`
  position: relative;
  z-index: 2;
  background: ${({ theme }) => theme.colors.white};
`;

const StyledTextField = styled(TextField)`
  ${mobile`
    font-size: 16px;
  `}
`;

const StyledPasswordField = styled(PasswordField)`
  ${mobile`
    font-size: 16px;
  `}
`;

const ButtonListWrapper = styled.div`
  width: 100%;
  position: relative;
`;

const ReferrerOptionDropdown = styled(Dropdown)``;

const ReferrerOptionDropdownButton = styled(Dropdown.Button)`
  cursor: pointer;
  font-family: inherit;
  border-radius: 4px;
  padding: 1px 16px;
  font-size: 16px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${({ theme }) => theme.colors.sublabelBlack};
  &[data-expanded='true'] {
    background-color: ${({ theme }) =>
      theme.colors.referrerOptionDropdownButtonBackground};
    color: ${({ theme }) => theme.colors.textLight};
    border-color: ${({ theme }) => theme.colors.referrerOptionDropdownFocus};
  }
  ${({ $hasError }) =>
    $hasError &&
    css`
      border-color: ${({ theme }) => theme.colors.errorMessage};
    `}
  ${({ $isDropdownOpen }) =>
    $isDropdownOpen &&
    css`
      border-bottom: 2px solid
        ${({ theme }) => theme.colors.referrerOptionDropdownFocus};
      padding: 0px 15px;
    `}
`;

const Chevron = styled(FaChevronDown)`
  padding-top: 5px;
  font-size: 20px;
  vertical-align: middle;
  transform: rotate(${({ $direction }) => ($direction === 'up' ? 180 : 0)}deg);
  transform-origin: 50% 50%;
  color: ${({ theme }) => theme.colors.sublabelBlack};
`;

const DropdownList = styled(Dropdown.List)`
  border: 1px solid
    ${({ theme }) => theme.colors.speedySignupDropdownListBorder};
  border-radius: 3px;
  max-height: 150px;
  overflow-y: scroll;
  position: absolute;
  right: 0;
  left: auto;
  z-index: 1;
  min-width: 100%;
  background-color: ${({ theme }) => theme.colors.palette.white};
`;

const DropdownOption = styled(Dropdown.ListItem)`
  padding: 16px 8px;
  font-size: 16px;
  &[aria-current='true'] {
    background-color: ${({ theme }) => theme.colors.speedySignupFormDropdownBg};
    color: ${({ theme }) => theme.colors.sublabelBlack};
  }
  &:hover,
  &:focus {
    color: ${({ theme }) => theme.colors.textEmphasized};
  }
  ${mobile`
    &[aria-current='true'] {
      background-color: ${({ theme }) =>
        theme.colors.speedySignupFormDropdownBg};
    }
    font-size: 16px;
  `}
`;

const ReferrerOptionDropdownLabel = styled.label`
  color: ${({ theme }) => theme.colors.speedySignupReferrerDropdownLabel};
  font-weight: 400;
  font-size: 16px;
  ${({ $isOptionSelected }) =>
    $isOptionSelected &&
    css`
      font-size: 12px;
    `}
`;

const ReferrerOptionValue = styled.div`
  line-height: 1.25;
  font-weight: 600;
  letter-spacing: ${-0.35 / 18}em;
`;

const DropdownLabelWrapper = styled.div`
  border: none;
  width: 100%;
  padding: 16px 0;
  ${({ $isOptionSelected }) =>
    $isOptionSelected &&
    css`
      padding: 7px 0;
    `}
`;

const SignupButton = styled(Button)`
  width: 100%;
  margin: 16px 0;
`;

const ErrorMessageWrapper = styled.div`
  margin-top: 9px;
  color: ${({ theme }) => theme.colors.errorMessage};
  font-size: 16px;
  line-height: 1.71;
  letter-spacing: ${-0.4 / 14}em;
`;

const ErrorIcon = styled(FaExclamationCircle)`
  margin-right: 4px;
  vertical-align: middle;
  margin-bottom: 3px;
  width: 16px;
`;

const CheckboxWrapper = styled.div`
  margin-top: 16px;
  display: flex;
`;
const ConsentCheckboxWrapper = styled(CheckboxWrapper)``;
const SubscribeToEmailCheckboxWrapper = styled(CheckboxWrapper)`
  align-items: center;
`;

const CheckBoxLabel = styled.label`
  margin-left: 8px;
  font-size: 12px;
  line-height: 1.33;
  letter-spacing: ${-0.3 / 12}em;
  text-align: left;
  color: ${({ theme }) => theme.colors.sublabelBlack};
`;

const TermsAndPrivacyLanguage = styled.p`
  margin-top: 12px;
  font-size: 12px;
  line-height: 1.33;
  letter-spacing: ${-0.3 / 12}em;
  text-align: left;
  color: ${({ theme }) => theme.colors.sublabelBlack};
`;

function SignupErrorMessage({ error }) {
  switch (error) {
    case SignupErrors.EMAIL_REQUIRED:
      return (
        <ErrorMessageWrapper data-autotag="SpeedySignupForm-email-error-msg">
          <ErrorIcon />
          <FormattedMessage
            id="speedy_signup.email_required"
            defaultMessage="Please enter an email address"
          />
        </ErrorMessageWrapper>
      );
    case SignupErrors.INVALID_EMAIL:
      return (
        <ErrorMessageWrapper data-autotag="SpeedySignupForm-email-error-msg">
          <ErrorIcon />
          <FormattedMessage
            id="speedy_signup.email_invalid"
            defaultMessage="Your email address is invalid. Please try again."
          />
        </ErrorMessageWrapper>
      );
    case SignupErrors.PASSWORD_REQUIRED:
      return (
        <ErrorMessageWrapper data-autotag="SpeedySignupForm-Password-error-msg">
          <ErrorIcon />
          <FormattedMessage
            id="speedy_signup.password_required"
            defaultMessage="Please enter a password"
          />
        </ErrorMessageWrapper>
      );
    case SignupErrors.PASSWORD_MIN_CHAR:
      return (
        <ErrorMessageWrapper data-autotag="SpeedySignupForm-Password-error-msg">
          <ErrorIcon />
          <FormattedMessage
            id="speedy_signup.password_min_6_char"
            defaultMessage="Password must be 6 characters minimum"
          />
        </ErrorMessageWrapper>
      );
    case SignupErrors.SIGNUP_FAILED:
      return (
        <ErrorMessageWrapper data-autotag="SpeedySignupForm-Signup-error-msg">
          <FormattedMessage
            id="app_global.404_error_message"
            defaultMessage="Something went wrong. We are working to fix it ASAP. Please try again!"
          />
        </ErrorMessageWrapper>
      );
    case SignupErrors.INVALID_ZIPCODE:
      return (
        <ErrorMessageWrapper data-autotag="SpeedySignupForm-Zipcode-error-msg">
          <ErrorIcon />
          <FormattedMessage
            id="speedy_signup.zipcode_invalid"
            defaultMessage="Please enter a valid zip code"
          />
        </ErrorMessageWrapper>
      );
    case SignupErrors.REFERRER_OPTION_REQUIRED:
      return (
        <ErrorMessageWrapper data-autotag="SpeedySignupForm-Hdyh-error-msg">
          <ErrorIcon />
          <FormattedMessage
            id="speedy_signup.referrer_option_required"
            defaultMessage="Please select an option"
          />
        </ErrorMessageWrapper>
      );
    case SignupErrors.CONSENT_REQUIRED:
      return (
        <ErrorMessageWrapper data-autotag="SpeedySignupForm-Consent-error-msg">
          <ErrorIcon />
          <FormattedMessage
            id="speedy_signup.accept_terms"
            defaultMessage="To continue please accept the terms of service and privacy policy."
          />
        </ErrorMessageWrapper>
      );
    case SignupErrors.DUPLICATE_ACCOUNT:
    case SignupErrors.USER_EXISTING:
      return (
        <DuplicateUserErrorWrapper data-autotag="SpeedySignupForm-Duplicate-error-msg">
          <FormattedMessage
            id="quiz.registration_screen_email_w_account_error"
            defaultMessage="Looks like that email already has an account with Yitty or Fabletics! Log in to that account to get access to both brands <i><a href='/login'>here</a></i>."
          />
        </DuplicateUserErrorWrapper>
      );
    case SignupErrors.GENERIC_ERROR:
      return (
        <ErrorMessageWrapper data-autotag="SpeedySignupForm-Generic-error-msg">
          <FormattedMessage
            id="speedy_signup.registration_screen_generic-error"
            defaultMessage="Please fix errors before continuing"
          />
        </ErrorMessageWrapper>
      );
    default:
      return null;
  }
}

SignupErrorMessage.propTypes = {
  error: PropTypes.string,
};

function SelectorDropdownButton({
  currentSelection,
  isHdyhRequired,
  hasError,
}) {
  const [dropdownState] = Dropdown.useDropdownContext();
  const isOptionSelected = !!currentSelection.value;
  const isDropdownOpen = dropdownState.isOpened;

  return (
    <ReferrerOptionDropdownButton
      data-autotag="SpeedySignupForm-Hdyh"
      $hasError={hasError}
      $isDropdownOpen={isDropdownOpen}
    >
      <DropdownLabelWrapper $isOptionSelected={isOptionSelected}>
        <ReferrerOptionDropdownLabel $isOptionSelected={isOptionSelected}>
          {isHdyhRequired ? (
            <FormattedMessage
              id="speedy_signup.how_did_you_hear_about_us_required"
              defaultMessage="How did you hear about us?*"
            />
          ) : (
            <FormattedMessage
              id="speedy_signup.how_did_you_hear_about_us_optional"
              defaultMessage="How did you hear about us?"
            />
          )}
        </ReferrerOptionDropdownLabel>
        {currentSelection.value ? (
          <ReferrerOptionValue>{currentSelection.label}</ReferrerOptionValue>
        ) : null}
      </DropdownLabelWrapper>
      <Chevron $direction={dropdownState.isOpened ? 'up' : 'down'} />
    </ReferrerOptionDropdownButton>
  );
}

SelectorDropdownButton.propTypes = {
  currentSelection: PropTypes.object,
  hasError: PropTypes.bool,
  isHdyhRequired: PropTypes.bool,
};

function SpeedySignupForm({ onWelcomeBackUser, onSignupSuccess, ...rest }) {
  const intl = useIntl();
  const actions = useAccountActions();
  const marketingActions = useMarketingActions();
  const { asPath, query } = useRouter();
  const { tld } = useDomain();
  const isUS = tld === '.com';
  const isHdyhRequired = getBrandValue({
    savage: true,
    default: isUS,
  });
  const { authFailed } = query;
  const checkBoxId = useId();
  const subscribeEmailId = useId();
  const showAuthFailed = truthyQueryParameter(authFailed);
  const { addToCartForSpeedySignup } = useProductDetailCTAContext();
  const productDetailContext = useProductDetailContext();
  const categoryIdList = productDetailContext.product.productCategoryIdList;
  const scrubsId = config.get('public.productCategories.scrubs');
  const isScrubs = categoryIdList?.includes(scrubsId);
  const scrubsCustomerReferralList = config.get('public.HDYH.scrubs');
  const customerReferrerList = isScrubs ? scrubsCustomerReferralList : '';
  // Note: This is not a quiz so it does not use useHDYHOptions
  const options = useReferrerOptions({ customerReferrerList });
  const { enabled: isPasswordlessSignupEnabled } = useHasFeature(
    featureNames.PASSWORDLESS_SIGNUP
  );
  const secondarySite = getSecondarySite(asPath);

  const isTPCheckboxSupported = isTermsAndPrivacyCheckboxSupported(tld);
  const isTPLinkSupported = isTermsAndPrivacyCopySupported(tld);

  const dispatch = useDispatch();

  const dropdownOptions = useMemo(() => {
    const defaultOption = {
      value: '',
      label: intl.formatMessage({
        id: 'site_signup_register.choose_opt',
        defaultMessage: 'Choose An Option',
      }),
    };
    const mappedOptions = options.map((option) => ({
      value: option.referrerId.toString(),
      label: option.label,
    }));
    mappedOptions.unshift(defaultOption);
    return mappedOptions;
  }, [intl, options]);

  const initialValues = useMemo(
    () => ({
      email: '',
      password: '',
      zipCode: '',
      referrerOption: { value: '', label: '' },
      consent: false,
      subscribeToEmail: false,
    }),
    []
  );

  const getProfileSizesFromSelectedProduct = useCallback(() => {
    const { selectionStateByProductIndex, isBundle, bundleProducts, product } =
      productDetailContext;
    const selectedSizes = {};
    const profile = {};
    let isMale;

    const mensDefaultProfileSizes = {
      size_bottom: ' ',
      size_top: ' ',
    };
    const womansDefaultProfileSizes = {
      size_bottom: ' ',
      size_top: ' ',
      size_bra: ' ',
    };

    if (isBundle) {
      // check for first item in bundle products.bundle can only be Male/Female
      const { isMens } = deriveProductCategory(bundleProducts[0]);
      isMale = isMens;
      // go over selected size of each item and derive new default profile sizes using the standard sizes
      Object.values(selectionStateByProductIndex).forEach(
        ({ selectedSKU, attributeFieldTypes }) => {
          const sizeField = attributeFieldTypes.SIZE;
          const selectedSize = selectedSKU.optionValuesByAttribute[sizeField];
          // `shoes` are available for VIP's only
          if (sizeField !== 'size' && sizeField !== 'size_shoe') {
            selectedSizes[sizeField] = getSizeByKey({
              isMens: isMens,
              category: sizeField,
              key: selectedSize,
            });
          }
        }
      );
      // Default mens profile sizes based on selected bundle product sizes
      if (isMens && !selectedSizes.size_bottom && selectedSizes.size_top) {
        selectedSizes.size_bottom = selectedSizes.size_top;
      } else if (
        isMens &&
        selectedSizes.size_bottom &&
        !selectedSizes.size_top
      ) {
        selectedSizes.size_top = selectedSizes.size_bottom;
      }

      // Default womans profile sizes based on selected bundle product sizes
      // If user selects `XL` bottoms and `XS` bra size , then we have to default top-size as `XS`
      if (!isMens && selectedSizes.size_top && !selectedSizes.size_bra) {
        selectedSizes.size_bra = selectedSizes.size_top;
      }
      if (!isMens && !selectedSizes.size_top && selectedSizes.size_bra) {
        selectedSizes.size_top = selectedSizes.size_bra;
      }
      if (
        !isMens &&
        selectedSizes.size_bottom &&
        !selectedSizes.size_bra &&
        !selectedSizes.size_top
      ) {
        selectedSizes.size_top = selectedSizes.size_bottom;
        selectedSizes.size_bra = selectedSizes.size_bottom;
      }
      if (!isMens && !selectedSizes.size_bottom) {
        selectedSizes.size_bottom = selectedSizes.size_top;
      }
    } else {
      const sizeField =
        selectionStateByProductIndex[0].attributeFieldTypes.SIZE;
      const itemSize =
        selectionStateByProductIndex[0].selectedSKU.optionValuesByAttribute[
          sizeField
        ];
      const { isMens } = deriveProductCategory(product);
      isMale = isMens;
      // `shoes` are available for VIP's only
      if (sizeField !== 'size' && sizeField !== 'size_shoe') {
        const size = getSizeByKey({
          isMens: isMens,
          category: sizeField,
          key: itemSize,
        });

        // if the sizeField is present in defaultProfileSizeMap, save the same size for others present
        // in defaultProfileSizeMap
        if (!isMens && womansDefaultProfileSizes[sizeField]) {
          Object.keys(womansDefaultProfileSizes).forEach(
            (sizeField) => (selectedSizes[sizeField] = size)
          );
        } else if (isMens && mensDefaultProfileSizes[sizeField]) {
          Object.keys(mensDefaultProfileSizes).forEach(
            (sizeField) => (selectedSizes[sizeField] = size)
          );
        } else {
          selectedSizes[sizeField] = size;
        }
      }
    }

    Object.keys(selectedSizes).forEach((item) => {
      profile[aggregationKeyMap[item]] = selectedSizes[item];
    });

    profile.gender = isMale ? Gender.MALE : Gender.FEMALE;
    return profile;
  }, [productDetailContext]);

  const { value: utmTerm } = useSessionDetail('utm_term');

  const handleFormSubmit = useCallback(
    async (values, p) => {
      const { setStatus, setSubmitting } = p;
      let error, statusCode, errorCode, errorData;
      setStatus({});

      // if it's a scrubs product toggle the disposition to a scrubs experience if necessary
      await validateSignupDisposition(
        productDetailContext,
        marketingActions,
        dispatch,
        tld
      );

      const customerData = {
        email: values.email,
        password: values.password || '',
        profile: getProfileSizesFromSelectedProduct(),
        details: {
          signup_source: 'speedy_signup',
          yitty_signup: 1,
          utm_term: utmTerm,
        },
      };

      if (values.zipCode) {
        customerData.profile.shipping_zip = values.zipCode;
      }

      if (values.referrerOption.value) {
        customerData.profile.customer_referrer_id = values.referrerOption.value;
      }

      if (!isUS) {
        customerData.optOutStatus = values.subscribeToEmail ? `none` : 'basic';
      }

      try {
        await actions.signUp(customerData);
        await dispatch(trackCompleteRegistration({ source: 'speedy' }));
        onSignupSuccess();
        await addToCartForSpeedySignup();
      } catch (err) {
        error = err;
        statusCode = err.statusCode;
        if (err.originalError && err.originalError.response) {
          try {
            const responseBody = await err.originalError.response.json();
            if (responseBody) {
              if (responseBody.statusCode) {
                statusCode = responseBody.statusCode;
              }
              if (responseBody.errorData) {
                errorCode = responseBody.errorCode;
                errorData = responseBody.errorData;
              }
            }
            await dispatch(
              trackRegistrationFailure({
                statusCode: statusCode,
                errorMessage: responseBody?.message,
                source: 'speedy',
              })
            );
            // eslint-disable-next-line no-empty
          } catch {}
        }
      }
      setSubmitting(false);

      if (error) {
        if ((!errorCode && statusCode === 400) || errorCode === 20000) {
          if (errorData) {
            const { code } = errorData[0];
            if (code === -32606) {
              if (isPasswordlessSignupEnabled) {
                onWelcomeBackUser();
                const loginRedirectUrl = getAutoLoginByEmailRedirect({
                  email: values.email,
                  pathPrefix: secondarySite ? `/${secondarySite}` : '',
                });
                window.location.href = loginRedirectUrl;
              } else {
                setStatus({ error: SignupErrors.USER_EXISTING });
              }
            } else {
              setStatus({ error: SignupErrors.GENERIC_ERROR });
            }
          }
        } else {
          setStatus({ error: SignupErrors.SIGNUP_FAILED });
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      productDetailContext,
      marketingActions,
      dispatch,
      tld,
      getProfileSizesFromSelectedProduct,
      utmTerm,
      isUS,
      actions,
      onSignupSuccess,
      addToCartForSpeedySignup,
      onWelcomeBackUser,
    ]
  );

  const initialStatus = useMemo(() => {
    if (showAuthFailed) {
      return { error: SignupErrors.SIGNUP_FAILED };
    }
    return {};
  }, [showAuthFailed]);

  const validate = useCallback(
    (values) => {
      const errors = {};

      if (!values.email) {
        errors.email = SignupErrors.EMAIL_REQUIRED;
      }
      if (
        !!values.email &&
        !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)
      ) {
        errors.email = SignupErrors.INVALID_EMAIL;
      }
      if (!isPasswordlessSignupEnabled) {
        if (!values.password) {
          errors.password = SignupErrors.PASSWORD_REQUIRED;
        } else if (
          values.password.length <
          config.get('public.passwordRequirements.minLength')
        ) {
          errors.password = SignupErrors.PASSWORD_MIN_CHAR;
        }
      }
      if (isUS && !values.zipCode) {
        errors.zipCode = SignupErrors.INVALID_ZIPCODE;
      }
      if (isUS && values.zipCode) {
        const stringZip = values.zipCode.toString();
        // savage supports mixed CA/US environments
        const isValid = getBrandValue({
          savage:
            /^[0-9]{5}(?:-[0-9]{4})?$/.test(stringZip) ||
            /^(?!.*[DFIOQU])[A-VXY][0-9][A-Z] ?[0-9][A-Z][0-9]$/i.test(
              stringZip
            ),
          default: /^[0-9]{5}(?:-[0-9]{4})?$/.test(stringZip),
        });
        if (!isValid) {
          errors.zipCode = SignupErrors.INVALID_ZIPCODE;
        }
      }
      if (!values.referrerOption.value && isHdyhRequired) {
        errors.referrerOption = SignupErrors.REFERRER_OPTION_REQUIRED;
      }
      if (!values.consent && isTPCheckboxSupported) {
        errors.consent = SignupErrors.CONSENT_REQUIRED;
      }
      return errors;
    },
    [isUS, isHdyhRequired, isTPCheckboxSupported, isPasswordlessSignupEnabled]
  );
  return (
    <Formik
      initialValues={initialValues}
      initialStatus={initialStatus}
      validate={validate}
      onSubmit={handleFormSubmit}
    >
      {({
        handleBlur,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue,
        errors,
        status,
        submitCount,
        values,
      }) => {
        const hasErrors = submitCount > 0;
        return (
          <Form name="signup" method="POST" onSubmit={handleSubmit} {...rest}>
            <StyledInputFields>
              <StyledTextField
                variant="standard"
                name="email"
                type="email"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                label={
                  <FormattedMessage
                    id="speedy_signup.email"
                    defaultMessage="Email*"
                  />
                }
                data-autotag="SpeedySignupForm-Email"
                error={!!errors.email && hasErrors}
              />
              {hasErrors && <SignupErrorMessage error={errors.email} />}
              {!isPasswordlessSignupEnabled && (
                <>
                  <StyledPasswordField
                    variant="standard"
                    name="password"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.password}
                    label={
                      <FormattedMessage
                        id="speedy_signup.password_6_char_min"
                        defaultMessage="Password (6 character minimum)*"
                      />
                    }
                    data-autotag="SpeedySignupForm-Password"
                    error={!!errors.password && hasErrors}
                  />
                  {hasErrors && <SignupErrorMessage error={errors.password} />}
                </>
              )}
              {isUS && (
                <>
                  <StyledTextField
                    variant="standard"
                    type="text"
                    name="zipCode"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.zipCode}
                    visibilityToggle={false}
                    label={
                      <FormattedMessage
                        id="speedy_signup.zipCode"
                        defaultMessage="Zip Code*"
                      />
                    }
                    data-autotag="SpeedySignupForm-Zipcode"
                    error={!!errors.zipCode && hasErrors}
                  />
                  {hasErrors && <SignupErrorMessage error={errors.zipCode} />}
                </>
              )}

              <ReferrerOptionDropdown
                onChange={(option) => setFieldValue('referrerOption', option)}
                value={values.referrerOption.value}
                options={dropdownOptions}
              >
                <ButtonListWrapper>
                  <SelectorDropdownButton
                    currentSelection={values.referrerOption}
                    isHdyhRequired={isHdyhRequired}
                    hasError={!!errors.referrerOption && hasErrors}
                  />
                  <DropdownList
                    data-scroll-lock-scrollable=""
                    data-autotag="pdp_speedy_sign_up_heard_from_list"
                  >
                    {dropdownOptions.map((option) => {
                      return (
                        <DropdownOption
                          value={option.value}
                          key={option.value}
                          data-selected={
                            option.value === values.referrerOption.value
                          }
                          data-autotag="pdp_speedy_sign_up_heard_from_option"
                        >
                          {option.label}
                        </DropdownOption>
                      );
                    })}
                  </DropdownList>
                </ButtonListWrapper>
              </ReferrerOptionDropdown>
            </StyledInputFields>
            {hasErrors && <SignupErrorMessage error={errors.referrerOption} />}
            {isTPCheckboxSupported && (
              <>
                <ConsentCheckboxWrapper>
                  <Checkbox
                    id={checkBoxId}
                    value={values.consent}
                    name="consent"
                    onChange={handleChange}
                    error={errors.consent && hasErrors}
                  />
                  <CheckBoxLabel htmlFor={checkBoxId}>
                    <FormattedMessage
                      id="speedy_signup.consent"
                      defaultMessage="You have read the Terms and Conditions and consent to the Privacy Policy."
                    />
                  </CheckBoxLabel>
                </ConsentCheckboxWrapper>
                {hasErrors && <SignupErrorMessage error={errors.consent} />}
              </>
            )}
            {(isTPCheckboxSupported || isTPLinkSupported) && (
              <SubscribeToEmailCheckboxWrapper>
                <Checkbox
                  id={subscribeEmailId}
                  value={values.subscribeToEmail}
                  name="subscribeToEmail"
                  onChange={handleChange}
                />
                <CheckBoxLabel htmlFor={subscribeEmailId}>
                  <FormattedMessage
                    id="speedy_signup.subscribe_to_email"
                    defaultMessage="Receive fashion tips and offers up to 50% off!"
                  />
                </CheckBoxLabel>
              </SubscribeToEmailCheckboxWrapper>
            )}
            {isTPLinkSupported && (
              <>
                <TermsAndPrivacyLanguage>
                  <FormattedMessage
                    id="speedy_signup.consent"
                    defaultMessage="You have read the Terms and Conditions and consent to the Privacy Policy."
                  />
                </TermsAndPrivacyLanguage>
                {hasErrors && <SignupErrorMessage error={errors.consent} />}
              </>
            )}
            {status.error && <SignupErrorMessage error={status.error} />}
            <SignupButton
              variant="primary"
              type="submit"
              isLoading={isSubmitting}
              data-autotag="pdp_speedy_sign_up_continue_btn"
            >
              <FormattedMessage
                id="speedy_signup.continue"
                defaultMessage="Continue"
              />
            </SignupButton>
          </Form>
        );
      }}
    </Formik>
  );
}

const validateSignupDisposition = async (
  productDetailContext,
  marketingActions,
  dispatch,
  tld
) => {
  // if it's a scrubs product toggle the disposition to a scrubs experience if necessary
  if (
    productDetailContext?.product?.productCategoryIdList?.indexOf(
      SCRUBS_PRODUCT_CATEGORY_ID
    ) >= 0
  ) {
    const disposition = await marketingActions.loadDirectMarketingDisposition();
    const experienceId = disposition?.payload.experience;

    if (experienceId !== SCRUBS_EXPERIENCE_ID) {
      const dmgCodes = config.get('public.dmgCodes')[tld];
      await dispatch(loadSession({ dmgCode: dmgCodes.scrubs }));
    }
  }
};

SpeedySignupForm.propTypes = {
  onSignupSuccess: PropTypes.func,
  onWelcomeBackUser: PropTypes.func,
};
export default SpeedySignupForm;
